import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingButton from "../common/LoadingButton";
import { setTransientMessage } from "./adminUtils";
import {
  ARTIFACT_TEMPLATE_KINDS,
  activateArtifactTemplate,
  createArtifactTemplateFromText,
  deactivateArtifactKind,
  deleteArtifactTemplate,
  listArtifactTemplates,
  previewCompileArtifactTemplate,
  uploadArtifactTemplate,
} from "./artifactTemplatesApi";

const STARTER_OUTLINES = {
  solution_blueprint: `# Solution Blueprint

## Overview
Purpose and scope of the solution.

## Architecture
Major components and how they relate.

## Integrations
Key interfaces and data flows.

## Non-functional Requirements
Security, performance, operability.

## Risks and Open Questions
Known gaps and decisions still needed.
`,
};

function ArtifactTemplatesManagement({ hideHeader = false }) {
  const [kind, setKind] = useState("solution_blueprint");
  const [templates, setTemplates] = useState([]);
  const [activeByKind, setActiveByKind] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [name, setName] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [activateOnSave, setActivateOnSave] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileInputRef = useRef(null);

  const kindMeta = useMemo(
    () => ARTIFACT_TEMPLATE_KINDS.find((k) => k.id === kind),
    [kind]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listArtifactTemplates(kind);
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
      setActiveByKind(data.active_by_kind || {});
    } catch (err) {
      setError(err.message || "Failed to load templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPreview(null);
    setSelectedFileName("");
    setSourceText("");
    setName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [kind]);

  const activeId = activeByKind?.[kind] || null;
  const usingDefault = !activeId;

  const onPreview = async () => {
    if (!sourceText.trim()) {
      setError("Paste template text to preview sections.");
      return;
    }
    setBusy("preview");
    setError(null);
    try {
      const data = await previewCompileArtifactTemplate({
        kind,
        name: name.trim() || `${kindMeta?.short || kind} template`,
        source_text: sourceText,
      });
      setPreview(data);
    } catch (err) {
      setError(err.message || "Preview failed");
      setPreview(null);
    } finally {
      setBusy(null);
    }
  };

  const onSaveText = async () => {
    if (!sourceText.trim()) {
      setError("Paste your template (Markdown with headings) before saving.");
      return;
    }
    setBusy("save");
    setError(null);
    try {
      await createArtifactTemplateFromText({
        kind,
        name: name.trim() || `${kindMeta?.short || kind} template`,
        source_text: sourceText,
        activate: activateOnSave,
      });
      setTransientMessage(setSuccess, "Template saved");
      setSourceText("");
      setName("");
      setPreview(null);
      await load();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setBusy(null);
    }
  };

  const onUpload = async () => {
    const chosen = fileInputRef.current?.files?.[0] || null;
    if (!chosen) {
      setError("Choose a .md or .docx file to upload.");
      return;
    }
    setBusy("upload");
    setError(null);
    setSuccess(null);
    try {
      const uploadName =
        name.trim() ||
        chosen.name.replace(/\.(md|markdown|docx|txt)$/i, "") ||
        `${kindMeta?.short || kind} template`;
      const result = await uploadArtifactTemplate({
        kind,
        name: uploadName,
        file: chosen,
        activate: activateOnSave,
      });
      const saved = result?.template;
      setTransientMessage(
        setSuccess,
        saved?.is_active
          ? `Uploaded and activated “${saved.name || uploadName}” (${
              saved.section_count || 0
            } sections)`
          : `Uploaded “${saved?.name || uploadName}” (${
              saved?.section_count || 0
            } sections)`
      );
      setSelectedFileName("");
      setName("");
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const onActivate = async (id) => {
    setBusy(`activate:${id}`);
    setError(null);
    try {
      await activateArtifactTemplate(id);
      setTransientMessage(setSuccess, "Template activated");
      await load();
    } catch (err) {
      setError(err.message || "Activate failed");
    } finally {
      setBusy(null);
    }
  };

  const requestDelete = (t) => {
    setError(null);
    setPendingDelete(t);
  };

  const cancelDelete = () => {
    if (busy && String(busy).startsWith("delete:")) return;
    setPendingDelete(null);
  };

  const confirmDelete = async () => {
    const t = pendingDelete;
    if (!t?.id) return;
    const deletedId = String(t.id);
    const deletedKind = t.kind || kind;
    setBusy(`delete:${deletedId}`);
    setError(null);
    // Optimistic remove so the row disappears immediately.
    setTemplates((prev) => prev.filter((x) => String(x.id) !== deletedId));
    if (t.is_active) {
      setActiveByKind((prev) => ({ ...prev, [deletedKind]: null }));
    }
    try {
      await deleteArtifactTemplate(deletedId);
      setPendingDelete(null);
      setTransientMessage(setSuccess, "Template deleted");
      await load();
    } catch (err) {
      setError(err.message || "Delete failed");
      await load();
    } finally {
      setBusy(null);
    }
  };

  const onUseDefault = async () => {
    setBusy("default");
    setError(null);
    try {
      await deactivateArtifactKind(kind);
      setTransientMessage(setSuccess, "Using product default template");
      await load();
    } catch (err) {
      setError(err.message || "Could not switch to default");
    } finally {
      setBusy(null);
    }
  };

  const insertStarter = () => {
    setSourceText(
      STARTER_OUTLINES[kind] || STARTER_OUTLINES.solution_blueprint
    );
    setName((n) => n || `Starter ${kindMeta?.short || kind}`);
    setPreview(null);
  };

  return (
    <div className="v2-admin-light space-y-5 p-4">
      {!hideHeader ? (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Blueprint templates
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Upload your organisation&apos;s Word or Markdown template for architecture
            decision records or blueprints. DecisionPen detects sections and fills them
            from your map. Word uploads keep headers, logos, and styles on generated
            documents.
          </p>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          Upload a Word or Markdown template. DecisionPen detects headings and maps
          facts from your repository into those sections. Word files keep their
          branding (headers, logos, styles) on generated documents.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {ARTIFACT_TEMPLATE_KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => setKind(k.id)}
            className={
              kind === k.id
                ? "rounded-lg border border-blue-600 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-800"
                : "rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            }
          >
            {k.short}
          </button>
        ))}
      </div>

      <div
        className={
          usingDefault
            ? "rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
            : "rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
        }
      >
        {usingDefault ? (
          <>
            <strong className="font-semibold">Product default</strong> is active
            for {kindMeta?.label || kind}. Upload and activate an organisation
            template to override it.
          </>
        ) : (
          <>
            <strong className="font-semibold">Organisation template</strong> is
            active for {kindMeta?.label || kind}.
            <button
              type="button"
              className="ml-3 text-emerald-800 underline hover:no-underline"
              onClick={onUseDefault}
              disabled={busy === "default"}
            >
              {busy === "default" ? "Switching…" : "Revert to product default"}
            </button>
          </>
        )}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {success}
        </div>
      ) : null}

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900">
          Add template — {kindMeta?.label || kind}
        </h3>
        <div className="mt-3 grid gap-3">
          <label className="block text-sm text-slate-700">
            Display name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g. ${kindMeta?.short || "Org"} house style`}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={activateOnSave}
                onChange={(e) => setActivateOnSave(e.target.checked)}
              />
              Activate after save
            </label>
            <button
              type="button"
              onClick={insertStarter}
              className="text-sm text-blue-700 underline hover:no-underline"
            >
              Insert starter outline
            </button>
          </div>

          <label className="block text-sm text-slate-700">
            Paste Markdown (use headings like ## Context)
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              rows={10}
              placeholder="## Context&#10;...&#10;## Decision&#10;..."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <LoadingButton
              type="button"
              onClick={onPreview}
              loading={busy === "preview"}
              loadingLabel="Previewing…"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-60"
            >
              Preview sections
            </LoadingButton>
            <LoadingButton
              type="button"
              onClick={onSaveText}
              loading={busy === "save"}
              loadingLabel="Saving…"
              className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              Save from text
            </LoadingButton>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <label className="block text-sm text-slate-700">
              Or upload a file (.md / .docx)
              <input
                key={`file-${kind}`}
                ref={fileInputRef}
                type="file"
                accept=".md,.markdown,.txt,.docx,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="mt-1 block w-full text-sm text-slate-600"
                onChange={(e) => {
                  const chosen = e.target.files?.[0] || null;
                  setSelectedFileName(chosen ? chosen.name : "");
                  setError(null);
                }}
              />
            </label>
            {selectedFileName ? (
              <p className="mt-1 text-xs text-slate-500">
                Selected: {selectedFileName}
              </p>
            ) : null}
            <LoadingButton
              type="button"
              onClick={onUpload}
              loading={busy === "upload"}
              loadingLabel="Uploading…"
              className="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
            >
              Upload file
            </LoadingButton>
          </div>
        </div>

        {preview ? (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-medium text-slate-800">
              Detected {preview.section_count} section
              {preview.section_count === 1 ? "" : "s"}
            </p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
              {(preview.sections_preview || []).map((s) => (
                <li key={s.id || s.title}>{s.title}</li>
              ))}
            </ol>
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">
            Saved templates
          </h3>
          <button
            type="button"
            onClick={load}
            className="text-sm text-slate-600 underline hover:no-underline"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="mt-3 text-sm text-slate-500">Loading…</p>
        ) : templates.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            No organisation templates yet for this kind.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {templates.map((t) => (
              <li
                key={t.id}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{t.name}</span>
                    {t.is_active ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                        Active
                      </span>
                    ) : null}
                    <span className="text-xs text-slate-500">
                      {t.section_count || 0} sections · {t.source_format}
                      {t.source_filename ? ` · ${t.source_filename}` : ""}
                    </span>
                  </div>
                  {(t.sections_preview || []).length > 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      {(t.sections_preview || [])
                        .map((s) => s.title)
                        .join(" · ")}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {!t.is_active ? (
                    <LoadingButton
                      type="button"
                      loading={busy === `activate:${t.id}`}
                      loadingLabel="Activating…"
                      onClick={() => onActivate(t.id)}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                    >
                      Activate
                    </LoadingButton>
                  ) : null}
                  <LoadingButton
                    type="button"
                    loading={busy === `delete:${t.id}`}
                    loadingLabel="Deleting…"
                    onClick={() => requestDelete(t)}
                    className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
                  >
                    Delete
                  </LoadingButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {pendingDelete ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-template-title"
          onClick={cancelDelete}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="delete-template-title"
              className="text-base font-semibold text-slate-900"
            >
              Delete template?
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {pendingDelete.is_active ? (
                <>
                  <span className="font-medium text-slate-800">
                    {pendingDelete.name || "This template"}
                  </span>{" "}
                  is currently active. After deletion, the product default will
                  be used until you activate another organisation template.
                </>
              ) : (
                <>
                  This will permanently remove{" "}
                  <span className="font-medium text-slate-800">
                    {pendingDelete.name || "this template"}
                  </span>
                  .
                </>
              )}
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={Boolean(busy && String(busy).startsWith("delete:"))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <LoadingButton
                type="button"
                loading={busy === `delete:${pendingDelete.id}`}
                loadingLabel="Deleting…"
                onClick={confirmDelete}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
                invert
              >
                Delete template
              </LoadingButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ArtifactTemplatesManagement;
